import React, { useState } from 'react';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../../api';
import { BaseModal } from '../../../ui/atoms/Modal/Modal';
import { useTranslation } from 'react-i18next';

const INPUT_CLASSES = "form-input-inset";

const SecurityTab = ({ 
  formData, 
  loading,
  setLoading,
  setSnackbarMessage,
  setSnackbarType,
  setShowSnackbar
}) => {
  const { t } = useTranslation();
  const confirmKeyword = 'DELETE';
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isArchivingAccount, setIsArchivingAccount] = useState(false);

  const handleArchiveAccount = async () => {
    if (confirmText.trim().toUpperCase() !== confirmKeyword) {
      setSnackbarMessage(t('userProfile.security.archive.confirmKeywordError', { keyword: confirmKeyword }));
      setSnackbarType('error');
      setShowSnackbar(true);
      return;
    }

    setIsArchivingAccount(true);
    setLoading(true);
    const token = localStorage.getItem('directus_token');
    
    try {

      // Users may only set their own status to archived (Directus policy); role changes are server-side.
      await axios.patch(`${DIRECTUS_INSTANCE}/users/me`, {
        status: 'archived'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSnackbarMessage(t('userProfile.security.archive.success'));
      setSnackbarType('success');
      setShowSnackbar(true);

      // Immediate local lockout.
      localStorage.removeItem('directus_token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('directus_token_expiry');
      localStorage.removeItem('user_id');
      window.location.href = '/signin';
    } catch (error) {
      console.error('Error archiving account:', error);
      setSnackbarMessage(t('userProfile.security.archive.error'));
      setSnackbarType('error');
      setShowSnackbar(true);
    } finally {
      setIsArchivingAccount(false);
      setLoading(false);
      setShowArchiveModal(false);
      setConfirmText('');
    }
  };

  return (
    <div className="space-y-12">
      <div className="pb-12">
        <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.security.title')}</h2>
        <p className="mt-4 text-sm leading-6 text-gray-600">
          {t('userProfile.security.description')}
        </p>

        <div className="mt-10">
          <div className="flex items-center gap-x-3">
            <input
              type="password"
              value={formData.widget_token || ''}
              readOnly
              className={`${INPUT_CLASSES} bg-gray-100`}
              id="widget-token"
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById('widget-token');
                input.type = input.type === 'password' ? 'text' : 'password';
              }}
              className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              {t('userProfile.security.showHide')}
            </button>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {formData.widget_token ? t('userProfile.security.tokenPresent') : t('userProfile.security.noToken')}
          </div>
        </div>
      </div>

      <div className="pb-12">
        <h3 className="text-2xl font-medium leading-7 text-gray-900">{t('userProfile.security.archive.title')}</h3>
        <p className="mt-4 text-sm leading-6 text-gray-600">
          {t('userProfile.security.archive.description')}
        </p>

        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            {t('userProfile.security.archive.warning')}
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setShowArchiveModal(true)}
              disabled={loading || isArchivingAccount}
              className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50"
            >
              {isArchivingAccount ? t('userProfile.security.archive.archiving') : t('userProfile.security.archive.deleteButton')}
            </button>
          </div>
        </div>
      </div>

      <BaseModal isOpen={showArchiveModal} onClose={() => !isArchivingAccount && setShowArchiveModal(false)} size="md">
        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
          <h4 className="text-base font-semibold text-gray-900">{t('userProfile.security.archive.modalTitle')}</h4>
          <p className="mt-2 text-sm text-gray-600">
            {t('userProfile.security.archive.modalDescription', { keyword: confirmKeyword })}
          </p>
          <div className="mt-4">
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className={INPUT_CLASSES}
              placeholder={confirmKeyword}
              disabled={isArchivingAccount}
            />
          </div>
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <button
            type="button"
            onClick={handleArchiveAccount}
            disabled={isArchivingAccount}
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 sm:ml-3 sm:w-auto"
          >
            {isArchivingAccount ? t('userProfile.security.archive.archiving') : t('userProfile.security.archive.confirmButton')}
          </button>
          <button
            type="button"
            onClick={() => setShowArchiveModal(false)}
            disabled={isArchivingAccount}
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:mt-0 sm:w-auto"
          >
            {t('common.cancel')}
          </button>
        </div>
      </BaseModal>
    </div>
  );
};

export default SecurityTab; 